import React, { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import 'leaflet-draw';
import { GeoJSONPolygon, PolygonDrawerProps } from "../../models/polygon";

function latLngsToGeoJSONPolygon(latlngs: L.LatLng[]): GeoJSONPolygon {
  const ring = latlngs.map(({ lat, lng }) => [lng, lat]);
  const first = ring[0];
  const closed = ring.length > 2 && (ring[ring.length - 1][0] !== first[0] || ring[ring.length - 1][1] !== first[1])
    ? [...ring, first]
    : ring;
  return { type: "Polygon", coordinates: [closed] };
}

function geoJSONToLatLngs(geo: GeoJSONPolygon | null): L.LatLng[] {
  if (!geo || geo.type !== "Polygon" || !geo.coordinates?.[0]) return [];
  return geo.coordinates[0].map(([lng, lat]) => new L.LatLng(lat, lng));
}

const DrawController: React.FC<Required<Pick<PolygonDrawerProps, "onPolygonChange" | "readOnly">> & { initialPolygon: GeoJSONPolygon | null; }> = ({ onPolygonChange, readOnly, initialPolygon }) => {
  const map = useMap();
  const drawnItemsRef = useRef<L.FeatureGroup>(new L.FeatureGroup());
  const drawControlRef = useRef<L.Control.Draw | null>(null);

// 1) Mount: add/remove the FeatureGroup to/from the map ONCE
useEffect(() => {
  const drawnItems = drawnItemsRef.current;
  map.addLayer(drawnItems);           // <-- without this, nothing renders
  return () => {
    map.removeLayer(drawnItems);
  };
}, [map]);

// 2) Sync with prop: reflect `initialPolygon` changes into the FeatureGroup
useEffect(() => {
  const drawnItems = drawnItemsRef.current;

  drawnItems.clearLayers();

  if (initialPolygon) {
    const latlngs = geoJSONToLatLngs(initialPolygon);
    if (latlngs.length) {
      const poly = new L.Polygon(latlngs, { color: "#3388ff" });
      drawnItems.addLayer(poly);
      drawnItems.bringToFront();              // optional, keeps it above tiles
      map.fitBounds(poly.getBounds(), { padding: [20, 20] });
    }
  }
}, [initialPolygon, map]);


  // Setup/teardown Draw control
  useEffect(() => {
    if (drawControlRef.current) {
      map.removeControl(drawControlRef.current);
      drawControlRef.current = null;
    }

    if (!readOnly) {
      const control = new L.Control.Draw({
        position: "topright",
        draw: {
          polygon: {
            allowIntersection: false,
            showArea: true,
            shapeOptions: { color: "#3388ff" },
          },
          polyline: false,
          circle: false,
          rectangle: false,
          circlemarker: false,
          marker: false,
        },
        edit: {
          featureGroup: drawnItemsRef.current,
          remove: true,
        },

      });
      drawControlRef.current = control;
      map.addControl(control);
    }

    return () => {
      if (drawControlRef.current) {
        map.removeControl(drawControlRef.current);
        drawControlRef.current = null;
      }
    };
  }, [map, readOnly]);

  // Event handlers for create/edit/delete
  useEffect(() => {
    const drawnItems = drawnItemsRef.current;

    function getCurrentPolygon(): GeoJSONPolygon | null {
      let polygon: GeoJSONPolygon | null = null;
      drawnItems.eachLayer(layer => {
        if (layer instanceof L.Polygon) {
          const latlngs = (layer as L.Polygon).getLatLngs();
          // LatLngs can be nested for multi-rings; we only allow a single ring UI here
          const firstRing = (latlngs as any)[0] as L.LatLng[];
          if (Array.isArray(firstRing) && firstRing.length >= 3) {
            polygon = latLngsToGeoJSONPolygon(firstRing);
          }
        }
      });
      return polygon;
    }

    const onCreated = (e: /* L.DrawEvents.Created */ any) => {
      drawnItems.clearLayers();
      drawnItems.addLayer(e.layer);
      onPolygonChange(getCurrentPolygon());
    };
    const onEdited = (_e: /* L.DrawEvents.Edited */ any) => {
      onPolygonChange(getCurrentPolygon());
    };
    const onDeleted = (_e: /* L.DrawEvents.Deleted */ any) => {
      onPolygonChange(getCurrentPolygon());
    };

    map.on('draw:created', onCreated as unknown as L.LeafletEventHandlerFn);
    map.on('draw:edited',  onEdited  as unknown as L.LeafletEventHandlerFn);
    map.on('draw:deleted', onDeleted as unknown as L.LeafletEventHandlerFn);

    return () => {
      map.off('draw:created', onCreated as unknown as L.LeafletEventHandlerFn);
      map.off('draw:edited',  onEdited  as unknown as L.LeafletEventHandlerFn);
      map.off('draw:deleted', onDeleted as unknown as L.LeafletEventHandlerFn);
    };
  }, [map, onPolygonChange]);

  return null;
};

export default DrawController
